import { useRef, useState } from "react";
import { setToken } from "@/shared/auth/tokenStorage";
import { useAlertDialog } from "@/shared/hooks/useSystemUI";
import { ApiError, toErrorMessage } from "@/shared/api/errors";
import { useAuthNavigation } from "@/features/auth/hooks/useAuthNavigation";
import { reviewLogin } from "@/features/auth/api/reviewLoginApi";

type UseReviewLoginReturn = {
  isPending: boolean;
  loginWithCredentials: (
    loginId: string,
    password: string,
    onSuccess?: () => void,
  ) => Promise<void>;
};

/**
 * review-login 실행 코어 — 숨김 ID/PW 폼(useLogin)과 dev 계정 원터치 버튼(LoginLanding)이 공유한다.
 *
 * dev 전용 `POST /auth/review/login` (심사/테스트 계정)을 호출한다. refresh token 없음(accessToken 단독).
 * 유저 정보(name·grade 등)는 이 훅이 채우지 않는다 — 셸 hydrate 단계에서 별도 처리한다.
 */
export function useReviewLogin(): UseReviewLoginReturn {
  const [isPending, setIsPending] = useState(false);
  // state 반영 전 연타를 막는 동기 가드. isPending 은 버튼 disabled/aria-busy 표시용으로 병행.
  const pendingRef = useRef(false);
  const { goAfterLogin } = useAuthNavigation();
  const { open: showError } = useAlertDialog();

  const loginWithCredentials = async (
    loginId: string,
    password: string,
    onSuccess?: () => void,
  ) => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setIsPending(true);
    try {
      const { accessToken } = await reviewLogin(loginId, password);
      setToken(accessToken); // review-login 은 refresh 없음 — refreshToken 인자 생략
      onSuccess?.();
      goAfterLogin();
    } catch (error) {
      const code = error instanceof ApiError ? error.errorCode : undefined;
      const message =
        code === "RATE_LIMIT_EXCEEDED"
          ? "요청이 많습니다. 잠시 후 다시 시도해주세요."
          : code === "INVALID_REQUEST"
            ? "아이디 또는 비밀번호를 확인해주세요."
            : toErrorMessage(error, "아이디 또는 비밀번호를 확인해주세요.");
      showError({ title: "로그인 실패", message });
    } finally {
      pendingRef.current = false;
      setIsPending(false);
    }
  };

  return { isPending, loginWithCredentials };
}
